import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'

import CarFileCardPriceBlock, {
  CarFileCardPriceDefinition,
  ExportPriceInfo,
} from 'components/molecules/carfile-card-price-block'
import PriceEvaluationIndicator from 'components/molecules/price-evaluation-indicator'
import { formatRoundedPrice, isPrice } from 'utilities/format'
import { hasPriceEvaluation } from 'utilities/price-evaluation'

const Rows = styled(CarFileCardPriceBlock)`
  flex-direction: column;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizeByFactor(1.25)};
`

const Divider = styled.span`
  display: block;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.brandDelta};
`

const StyledPriceEvaluationIndicator = styled(PriceEvaluationIndicator)`
  margin-left: auto;
`

/**
 * Pricing section of the car file card in the grid view: the selling price and
 * margin on the first row, the export price and pricing evaluation on the
 * second row.
 */
const CarFileCardPricing = ({
  primaryLabel,
  primaryValue,
  margin,
  exportPrice,
  priceEvaluation,
  exportPriceInclVat,
  onPriceEvaluationInfoClick,
  children,
  className,
  ...restProps
}) => {
  const { t } = useTranslation()
  const hasMargin = margin !== null && margin !== undefined && margin !== ''
  const showExportPrice = isPrice(exportPrice)
  const showEvaluation = hasPriceEvaluation(priceEvaluation)

  return (
    <Rows className={className} {...restProps}>
      <Row>
        <CarFileCardPriceDefinition label={primaryLabel} value={primaryValue} />
        {hasMargin && (
          <CarFileCardPriceDefinition
            label={t('margin')}
            value={
              isPrice(margin) ? formatRoundedPrice(margin) : String(margin)
            }
            alignEnd
          />
        )}
      </Row>
      {(showExportPrice || showEvaluation) && (
        <>
          <Divider />
          <Row>
            {showExportPrice && (
              <CarFileCardPriceDefinition
                label={t('exportPrice')}
                value={formatRoundedPrice(exportPrice)}
                info={<ExportPriceInfo inclVat={exportPriceInclVat} />}
                gap={0.5}
              />
            )}
            {showEvaluation && (
              <StyledPriceEvaluationIndicator
                evaluation={priceEvaluation}
                onInfoClick={onPriceEvaluationInfoClick}
              />
            )}
          </Row>
        </>
      )}
      {children}
    </Rows>
  )
}

CarFileCardPricing.propTypes = {
  primaryLabel: PropTypes.string.isRequired,
  primaryValue: PropTypes.string.isRequired,
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  exportPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  priceEvaluation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** True when the export price includes VAT (exportprijs), false for exportprijs_exbtw */
  exportPriceInclVat: PropTypes.bool,
  onPriceEvaluationInfoClick: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
}

CarFileCardPricing.defaultProps = {
  margin: null,
  exportPrice: null,
  priceEvaluation: null,
  exportPriceInclVat: false,
  onPriceEvaluationInfoClick: null,
  children: null,
  className: null,
}

export default CarFileCardPricing
